import * as React from "react";
import { Elements, ElementsThemed, Fragments, HOCs } from "components";
import { Order, Login, Logout } from "pages";
import { config, constants, Copy, Routes, images } from "utils";
import { matchesRegex } from "utils/helpers/string";
import { NavIcon } from "@lunchboxinc/lunchbox-components-v2/dist/components";
import BackButton from "../../../components/BackButton/BackButton";
import NavDrawer from "../../navDrawer";

import theme1 from "../header.module.scss";
import theme2 from "../headerTheme2.module.scss";

import type { HeaderProps } from "../../header";

const {
  Logo,
  Icon,
  Condition: { Condition: If },
} = Elements;
const { Text, View, Button } = ElementsThemed;
const { Dropdown, Drawer } = Fragments;
const { withTemplate, withCSSTheme } = HOCs;
const {
  ORDER_TYPES: { KIOSK },
} = constants;
const loginIcon = config?.images?.icon_login;
const restaurantIcon = config?.images?.art_logo;
const profileIcon = config?.images?.icon_profile;
const logoutIcon = config?.images?.icon_logout;
const backButton = config?.images?.button_back;

function Header({
  cartCount,
  cartIconSrc,
  css,
  diningOption,
  history,
  isCartDrawerOpen,
  isLoginDrawerOpen,
  isLogoutDrawerOpen,
  location,
  orderContext,
  patronContext,
  regexpsBack,
  regexpsBackCheckout,
  regexpsBackMenu,
  regexpsCart,
  regexpsLogo,
  regexpsMarket,
  routerLocation,
  setIsLoginDrawerOpen,
  setIsLogoutDrawerOpen,
  style,
  toggleLoginDrawer,
  toggleLogoutDrawer,
  trackerPath,
  regexpsMenuDiscovery,
  handleAccountClick,
  isOpen,
}: HeaderProps) {
  const { views, labels, buttons } = style;
  const iconType =
    config.theme.header.nav_icon === "Type2" ? buttons.nav : labels.primary;
  const activeResGroupId =
    routerLocation.pathname.split("/")[
      routerLocation.pathname.split("/").length - 1
    ];

  const handleGoBack = () => {
    if (matchesRegex(regexpsLogo, routerLocation.pathname)) {
      return history.push(
        `${Routes.MARKET_PLACE(orderContext.location.slug, diningOption)}`,
      );
    }
    if (matchesRegex(regexpsMarket, routerLocation.pathname)) {
      return history.push(`${Routes.ROOT}`);
    }
    return history.goBack();
  };

  return (
    <View
      id="header"
      type={views.background}
      Component="header"
      className={css.header}
    >
      <div className={css["header-wrapper"]} id="header-wrapper">
        <div className={css["header-wrapper--left"]}>
          {/* Only show the logo if on the home page or on the location page and not pocket kiosk */}
          <If
            is={
              !matchesRegex(regexpsLogo, routerLocation.pathname) ||
              (matchesRegex(regexpsLogo, routerLocation.pathname) &&
                diningOption !== KIOSK)
            }
          >
            <div className={css.logo}>
              <a href={config.website.url}>
                <Logo src={images?.art_logo ?? restaurantIcon} />
              </a>
            </div>
          </If>

          <If is={matchesRegex(regexpsBackMenu, routerLocation.pathname)}>
            <BackButton
              imgSrc={images?.button_back_main ?? backButton}
              className={css["nav-back"]}
              onClick={() => history.goBack()}
            />
            <div className={css["nav-info"]}>
              <Text type={labels.secondary}>
                {Copy.HEADER_STATIC.GO_BACK_TO_MENU_TEXT}
              </Text>
            </div>
          </If>

          <If is={matchesRegex(regexpsBackCheckout, routerLocation.pathname)}>
            <BackButton
              imgSrc={images?.button_back_main ?? backButton}
              className={css["nav-back"]}
              onClick={() => history.push("/")}
            />
            <div className={css["nav-info"]}>
              <Text type={labels.tertiary}>
                {Copy.HEADER_STATIC.START_A_NEW_ORDER_TEXT}
              </Text>
            </div>
          </If>
        </div>

        <div className={`${css["header-wrapper--right"]}`}>
          <If is={!config.routes || (config.routes && config.routes.ordering)}>
            <If
              is={
                !patronContext.isLoggedIn &&
                !matchesRegex([/^\/sign-up/], routerLocation.pathname)
              }
            >
              <NavIcon
                iconType={iconType}
                themeType={config.theme.header.nav_icon || "Type1"}
                restaurantName={config.restaurant}
                symbol="user"
                iconSrc={config?.images?.login || loginIcon}
                onClick={toggleLoginDrawer}
              >
                <button
                  onClick={toggleLoginDrawer}
                  data-cy="login-icon"
                  type="submit"
                  className={css["header-wrapper--headerLoginButton"]}
                >
                  {Copy.HEADER_STATIC.LOGIN_TEXT}
                </button>
              </NavIcon>
            </If>

            {patronContext.isLoggedIn && config.theme.account?.drawer && (
              <NavIcon
                iconType={iconType}
                themeType={config.theme.header.nav_icon || "Type1"}
                restaurantName={config.restaurant}
                symbol="user"
                iconSrc={config?.images?.login || loginIcon}
                datatest="account-icon"
                onClick={handleAccountClick}
              >
                <button
                  type="button"
                  aria-expanded={isOpen}
                  className={css["header-wrapper--accountDropdown"]}
                >
                  {Copy.HEADER_STATIC.ACCOUNT_TEXT}
                </button>
              </NavIcon>
            )}

            <If is={patronContext.isLoggedIn && !config.theme.account?.drawer}>
              <Dropdown
                className={css.dropdown}
                trigger={
                  <NavIcon
                    iconType={iconType}
                    themeType={config.theme.header.nav_icon || "Type1"}
                    restaurantName={config.restaurant}
                    symbol="user"
                    iconSrc={config?.images?.login || loginIcon}
                    data-cy="account-icon"
                  >
                    ACCOUNT
                  </NavIcon>
                }
              >
                <DropdownItem
                  text="Profile"
                  type={buttons.dropdown}
                  css={css}
                  icon={config?.images?.login || profileIcon}
                  onClick={() => history.push("/profile")}
                />
                <DropdownItem
                  text={Copy.HEADER_STATIC.LOGOUT_TEXT}
                  type={buttons.dropdown}
                  css={css}
                  icon={logoutIcon}
                  onClick={toggleLogoutDrawer}
                />
              </Dropdown>
            </If>
          </If>
          <If is={!matchesRegex(regexpsCart, routerLocation.pathname)}>
            <NavDrawer
              iconType={iconType}
              text={
                orderContext.order.diningOption === KIOSK
                  ? Copy.HEADER_STATIC.CHECKOUT_TEXT
                  : Copy.HEADER_STATIC.CART_TEXT
              }
              symbol="cart"
              icon={cartIconSrc}
              isOpen={isCartDrawerOpen}
              css={css}
              cartCount={cartCount}
            >
              {({ close }) => (
                <Order
                  orderContext={orderContext}
                  patronContext={patronContext}
                  history={history}
                  location={routerLocation}
                  close={close}
                  isLoggedIn={patronContext.isLoggedIn}
                  isEmployee={patronContext.patron.isEmployee}
                />
              )}
            </NavDrawer>
          </If>

          <Drawer.ResponsiveDrawer
            isOpen={isLoginDrawerOpen}
            onChange={setIsLoginDrawerOpen}
            ariaLabel="Login"
          >
            <Login
              onComplete={() => setIsLoginDrawerOpen(false)}
              trackerPath={trackerPath}
            />
          </Drawer.ResponsiveDrawer>

          <Drawer.ResponsiveDrawer
            isOpen={isLogoutDrawerOpen}
            onChange={setIsLogoutDrawerOpen}
            ariaLabel="Logout"
          >
            <Logout
              onConfirm={() => {
                setIsLogoutDrawerOpen(false);
                patronContext.logout();
              }}
              onCancel={() => setIsLogoutDrawerOpen(false)}
            />
          </Drawer.ResponsiveDrawer>
        </div>
      </div>
      {/* Hide the secondwrwpper on menu-discovery page */}
      {matchesRegex(regexpsMenuDiscovery, routerLocation.pathname) ? null : (
        <If is={!matchesRegex(regexpsBack, routerLocation.pathname)}>
          <div className={css["header-secondWrapper"]} id="second-wrapper">
            <BackButton
              imgSrc={images?.button_back_main_secondary ?? backButton}
              className={css["header-secondWrapper-back"]}
              onClick={handleGoBack}
            />
            <View
              type={views.background}
              layout="horizontal"
              className={css["header-secondWrapper-content"]}
            >
              <Text
                type={labels.quaternary || labels.primary}
                className={css["header-secondWrapper-content-text"]}
                dangerouslySetInnerHTML={{
                  __html: Copy.HEADER_STATIC.MARKET_PLACE_NAV_TEXT,
                }}
              />
              <div className={css["header-secondWrapper-content-logos"]}>
                {location?.restaurantGroups?.length &&
                  location.restaurantGroups.map((group) => {
                    const isActive = activeResGroupId === group.id;
                    return (
                      // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
                      <img
                        className={
                          isActive &&
                          css["header-secondWrapper-content-logos-selected"]
                        }
                        onClick={() =>
                          history.replace(
                            `${Routes.LOCATION_INFO({
                              orderType: diningOption,
                              resGroupId: group.id,
                              slug: orderContext.location.slug,
                            })}`,
                          )
                        }
                        onKeyDown={() =>
                          history.replace(
                            `${Routes.LOCATION_INFO({
                              orderType: diningOption,
                              resGroupId: group.id,
                              slug: orderContext.location.slug,
                            })}`,
                          )
                        }
                        alt={
                          isActive ? "restaurantSelectedIcon" : "restaurantIcon"
                        }
                        src={
                          isActive
                            ? group.media.restaurantSelectedIcon
                            : group.media.restaurantIcon
                        }
                      />
                    );
                  })}
              </div>
            </View>
          </div>
        </If>
      )}
    </View>
  );
}

const cssTheme = () => {
  switch (config.theme.header.theme) {
    case "Theme2":
      return theme2;
    default:
      return theme1;
  }
};

interface DropdownItemProps {
  text: string;
  type: string;
  css: Record<string, string>;
  icon: string;
  onClick: () => void;
}

const DropdownItem = withCSSTheme(
  ({ text, type, onClick, icon, css }: DropdownItemProps) => (
    <Button className={css["dropdown-item"]} type={type} onClick={onClick}>
      {text}
      {icon && <Icon src={icon} />}
    </Button>
  ),
  cssTheme(),
);

export default withTemplate(withCSSTheme(Header, cssTheme()), "main");
